import type { Member } from '~/shared/types/member.js'

/**
 * Tracks whether the current member's type or SubKrewe has changed between
 * store mutations.
 *
 * Extracted from `useCoreSubscriptions` because the whole point of that
 * subscription is this comparison, and it was wrong in a way that cost real
 * money: it tried to read the previous member out of `mutation.payload` when
 * `mutation.type === 'patch'`. Pinia's mutation types are 'direct',
 * 'patch object' and 'patch function' — never 'patch' — so the previous member
 * was always null, `oldSubkreweId` always undefined, and the SubKrewe
 * comparison therefore true on *every* mutation for anyone in a SubKrewe. Each
 * one tore down and re-created every Firestore listener, and the refresh
 * itself writes to the members store, so it fed its own trigger.
 *
 * Reading the payload could never have worked: these stores patch with a
 * function, whose payload is the callback rather than the state. The previous
 * values have to be remembered, which is what this does.
 */

export interface MemberChange {
  typeChanged: boolean
  subkreweChanged: boolean
  /** The type observed before this change, for logging. */
  previousType: Member['member_type']
}

export interface MemberChangeTracker {
  /**
   * Records an observation and reports what changed since the last one.
   *
   * Returns `null` for the first observation — the initial load is not a
   * change — and for any observation where nothing relevant moved.
   */
  observe: (member: Pick<Member, 'member_type' | 'subkrewe'>) => MemberChange | null
}

export function createMemberChangeTracker(): MemberChangeTracker {
  let seen = false
  let previousType: Member['member_type']
  let previousSubkreweId: string | undefined

  return {
    observe(member) {
      const currentType = member.member_type
      const currentSubkreweId = member.subkrewe?.id

      // Seed on first observation so the initial load is not a change.
      if (!seen) {
        seen = true
        previousType = currentType
        previousSubkreweId = currentSubkreweId
        return null
      }

      const priorType = previousType
      const typeChanged = currentType !== priorType
      const subkreweChanged = currentSubkreweId !== previousSubkreweId

      previousType = currentType
      previousSubkreweId = currentSubkreweId

      if (!typeChanged && !subkreweChanged) return null

      return { typeChanged, subkreweChanged, previousType: priorType }
    },
  }
}
